import { mutateDocuments } from "@/api/documentFile/useS3Query";
import { CopyForm } from "@/features/documentFile/form/CopyForm";
import { CreateFolderForm } from "@/features/documentFile/form/CreateFolderForm";
import { MoveForm } from "@/features/documentFile/form/MoveForm";
import { RenameForm } from "@/features/documentFile/form/RenameForm";
import { UploadForm } from "@/features/documentFile/form/UploadForm";
import { useModal } from "@/features/modal/ModalStackManager";
import { useStandardLayout } from "@/features/standardLayout/Context";
import { ListTable } from "@/features/standardLayout/ListTable";
import { PaginationBox } from "@/features/standardLayout/PaginationBox";
import { StandardLayoutTableWrapper } from "@/features/standardLayout/Styles";
import customAlert from "@/features/ui/alert/Alert";
import { useLoader } from "@/hooks/useLoader";
import { BASE_URL } from "@/instance/axios";
import { ActionIcon, Button, Flex, Group, rem, Text } from "@mantine/core";
import {
  S3ApiS3DeleteDeleteRequest,
  S3ListGet200ResponseInner,
} from "@sizlcorp/sizl-api-document/dist/models";
import {
  IconArrowBackUp,
  IconArrowRight,
  IconCopy,
  IconDownload,
  IconFolder,
  IconStatusChange,
  IconTrash,
  IconUpload,
} from "@tabler/icons-react";
import { useMutation } from "@tanstack/react-query";
import axios from "axios";
import dayjs from "dayjs";
import saveAs from "file-saver";
import { useEffect, useMemo, useState } from "react";
import { Column, SelectColumn } from "react-data-grid";

// ListTable의 TResponse용
interface S3ListGet200ResponseForListTable {
  rows?: S3ListGet200ResponseInner[];
  total?: number;
  page?: number;
  pageSize?: number;
  totalPages?: number;
}

export const DocumentFilePage = () => {
  const { openModal, closeModal } = useModal();
  const { selectedRows, refetch, setSelectedRows, setParams, data, isFetching } =
    useStandardLayout();
  const { setLoading, loading, LoadingOverlay } = useLoader();
  const [currentPath, setCurrentPath] = useState<string | undefined>("");
  const [previewItem, setPreviewItem] = useState<S3ListGet200ResponseInner | null>(null);

  const rowKeyGetter = (row: S3ListGet200ResponseInner) => row.Key;

  const rows = useMemo(() => {
    if (isFetching) return [];
    if (Array.isArray(data)) return data;
    if (Array.isArray((data as any)?.data)) return (data as any).data;
    if (Array.isArray((data as any)?.rows)) return (data as any).rows;
    return [];
  }, [data, isFetching]);

  useEffect(() => {
    setLoading(true);
    setParams((prev) => ({ ...prev, prefix: currentPath }));
  }, [currentPath]);

  useEffect(() => {
    if (!isFetching) setLoading(false);
  }, [isFetching]);

  const { mutate: deleteMutate } = useMutation(
    (params: S3ApiS3DeleteDeleteRequest) => mutateDocuments.delete(params).mutationFn(undefined),
    {
      onSuccess: () => {
        customAlert("파일 삭제에 성공하였습니다.", "파일 삭제", "green");
        refetch();
        setSelectedRows(new Set());
      },
      onError: (error: any) => {
        customAlert(
          error.response?.data?.message ?? "파일 삭제에 실패하였습니다.",
          "파일 삭제 실패",
          "red",
        );
      },
    },
  );

  const handleDeleteItems = async (items: S3ListGet200ResponseInner[]) => {
    await Promise.all(
      items.map((item) =>
        deleteMutate({
          s3DeleteDeleteRequest: {
            name: item.Key ?? "",
          },
        }),
      ),
    );
  };

  const getFileNameFromKey = (key: string): string => {
    const index = key.lastIndexOf("/");
    return index === -1 ? key : key.substring(index + 1);
  };

  const handleDownloads = async (items: S3ListGet200ResponseInner[]) => {
    const keys = items.map((x) => x.Key);

    const paramsKeys = items.map((item) => item.Key ?? "").filter((key) => key !== "");

    const fileName = keys.length > 1 ? "file.zip" : getFileNameFromKey(keys[0] ?? "");

    try {
      const response = await axios.post(
        `${BASE_URL}/s3/download`,
        {
          keys: paramsKeys,
        },
        {
          responseType: "blob",
          headers: {
            Authorization: "Bearer " + localStorage.getItem("authToken"),
          },
        },
      );
      customAlert("파일 다운로드가 완료되었습니다..", "다운로드", "green");
      setSelectedRows(new Set());
      saveAs(response.data, fileName);
    } catch (error: any) {
      console.error("Download failed:", error);
      customAlert(
        error.response?.data?.message ?? "파일 다운로드에 실패하였습니다.",
        "다운로드 실패",
        "red",
      );
      throw new Error(error.message);
    }
  };

  const columns: readonly Column<S3ListGet200ResponseInner>[] = useMemo(
    () => [
      {
        ...SelectColumn,
        resizable: true,
      },
      {
        key: "name",
        name: "이름",
        sortable: true,
        resizable: true,
        formatter: (formatterProps) => {
          return (
            <Group spacing="xs">
              {formatterProps.row.isDirectory ? <IconFolder size={16} /> : null}
              <Text
                onClick={() => {
                  if (formatterProps.row.isDirectory) setCurrentPath(formatterProps.row.Key);
                  else if (formatterProps.row.Key?.match(/\.(jpg|jpeg|png|gif)$/))
                    setPreviewItem(formatterProps.row);
                }}
                style={{ cursor: "pointer" }}
              >
                {formatterProps.row.name}
              </Text>
            </Group>
          );
        },
      },
      {
        key: "LastModified",
        name: "수정일",
        sortable: true,
        resizable: true,
        formatter: (formatterProps) => {
          return (
            <Text>
              {formatterProps.row.LastModified
                ? dayjs(formatterProps.row.LastModified).format("YYYY-MM-DD")
                : "-"}
            </Text>
          );
        },
      },
      {
        key: "Size",
        name: "크기",
        sortable: true,
        resizable: true,
        formatter: (formatterProps) => {
          return (
            <Flex align="center" justify="center" gap="xs">
              <Text>
                {formatterProps.row.Size
                  ? `${(formatterProps.row.Size / 1024).toFixed(1)} KB`
                  : "-"}
              </Text>
            </Flex>
          );
        },
      },
      {
        key: "options",
        name: "동작",
        sortable: true,
        resizable: true,
        formatter: (formatterProps: any) => {
          return (
            <Flex w="100%" h="100%" justify="center" align="center" gap="md">
              {!formatterProps.row.isDirectory &&
                formatterProps.row.url?.match(/\.(jpg|jpeg|png|gif)$/) && (
                  <ActionIcon
                    variant="light"
                    color="blue"
                    onClick={() => setPreviewItem(formatterProps.row)}
                  >
                    <IconFolder />
                  </ActionIcon>
                )}

              {!formatterProps.row.isDirectory && (
                <ActionIcon variant="light" onClick={() => handleDownloads([formatterProps.row])}>
                  <IconDownload />
                </ActionIcon>
              )}
              <ActionIcon
                variant="light"
                color="red"
                onClick={() => handleDeleteItems([formatterProps.row])}
              >
                <IconTrash />
              </ActionIcon>
            </Flex>
          );
        },
      },
    ],
    [closeModal, openModal],
  );

  return (
    <Flex w="100%" h="100%" gap="xs" direction="column" p="sm">
      <Flex bg="white" px="sm" direction="column" gap="xs">
        <Text fz="xl" fw={600}>
          문서 관리
        </Text>
        <Text fz="xs">우리 회사에서 다루는 문서에 대해 조회합니다.</Text>
      </Flex>
      <Group position="apart" mb="md" px="sm">
        {selectedRows.size > 0 ? (
          <Flex gap="xs">
            <Button
              leftIcon={<IconArrowRight size={rem(16)} />}
              color="green"
              disabled={selectedRows.size !== 1}
              onClick={() => {
                const selected = Array.from(selectedRows.entries())
                  .map(([key]) =>
                    rows?.find((file: S3ListGet200ResponseInner) => key === rowKeyGetter(file)),
                  )
                  .filter((file): file is S3ListGet200ResponseInner => !!file);
                openModal(
                  <MoveForm data={rows} selected={selected} refetch={refetch} />,
                  "",
                  "파일 이동",
                ).then(() => {
                  setSelectedRows(new Set());
                  setCurrentPath("");
                  setParams((prev) => ({ ...prev, prefix: "" }));
                  refetch();
                });
              }}
            >
              이동
            </Button>
            <Button
              leftIcon={<IconCopy size={rem(16)} />}
              color="indigo"
              disabled={selectedRows.size !== 1}
              onClick={() => {
                const selected = Array.from(selectedRows.entries())
                  .map(([key]) =>
                    rows?.find((file: S3ListGet200ResponseInner) => key === rowKeyGetter(file)),
                  )
                  .filter((file): file is S3ListGet200ResponseInner => !!file);
                openModal(
                  <CopyForm data={rows} selected={selected} refetch={refetch} />,
                  "",
                  "파일 복사",
                ).then(() => {
                  setSelectedRows(new Set());
                  setCurrentPath("");
                  setParams((prev) => ({ ...prev, prefix: "" }));
                  refetch();
                });
              }}
            >
              복사
            </Button>
            <Button
              leftIcon={<IconStatusChange size={rem(16)} />}
              color="violet"
              disabled={selectedRows.size !== 1}
              onClick={() => {
                const selected = Array.from(selectedRows.entries())
                  .map(([key]) =>
                    rows?.find((file: S3ListGet200ResponseInner) => key === rowKeyGetter(file)),
                  )
                  .filter((file): file is S3ListGet200ResponseInner => !!file);
                openModal(
                  <RenameForm selected={selected} refetch={refetch} />,
                  "",
                  "이름 변경",
                ).then(() => {
                  setSelectedRows(new Set());
                  setCurrentPath("");
                  setParams((prev) => ({ ...prev, prefix: "" }));
                  refetch();
                });
              }}
            >
              이름 변경
            </Button>
          </Flex>
        ) : (
          <Flex gap="xs">
            <Button
              color="violet"
              leftIcon={<IconFolder size={rem(16)} />}
              onClick={() =>
                openModal(
                  <CreateFolderForm currentPath={currentPath} refetch={refetch} />,
                  "",
                  "새 폴더 생성",
                )
              }
            >
              새 폴더 만들기
            </Button>
            <Button
              color="cyan"
              leftIcon={<IconUpload size={rem(16)} />}
              onClick={() =>
                openModal(
                  <UploadForm currentPath={currentPath} refetch={refetch} />,
                  "",
                  "파일 업로드",
                )
              }
            >
              업로드
            </Button>
            <Button
              color="lime"
              leftIcon={<IconArrowBackUp size={rem(16)} />}
              onClick={() => {
                setCurrentPath("");
              }}
            >
              루트로 이동
            </Button>
          </Flex>
        )}

        <Flex gap="xs">
          <Button
            leftIcon={<IconDownload size={rem(16)} />}
            disabled={
              rows.length === 0 ||
              Array.from(selectedRows.entries())
                .map(([key]) =>
                  rows.find((file: S3ListGet200ResponseInner) => key === rowKeyGetter(file)),
                )
                .filter((file): file is S3ListGet200ResponseInner => !!file)
                .some((file) => file.isDirectory)
            }
            onClick={() => {
              const selected = Array.from(selectedRows.entries())
                .map(([key]) =>
                  rows?.find((file: S3ListGet200ResponseInner) => key === rowKeyGetter(file)),
                )
                .filter((file): file is S3ListGet200ResponseInner => !!file);
              if (selected.length > 0) {
                handleDownloads(selected);
              } else {
                customAlert("선택된 항목이 없습니다.", "다운로드", "red");
              }
            }}
          >
            선택된 행 다운
          </Button>
          <Button
            leftIcon={<IconTrash size={rem(16)} />}
            color="red"
            onClick={() => {
              const selected = Array.from(selectedRows.entries())
                .map(([key]) =>
                  rows?.find((file: S3ListGet200ResponseInner) => key === rowKeyGetter(file)),
                )
                .filter((file): file is S3ListGet200ResponseInner => !!file);
              if (selected.length > 0) {
                handleDeleteItems(selected);
              } else {
                customAlert("선택된 항목이 없습니다.", "파일삭제", "red");
              }
            }}
          >
            선택된 행 삭제
          </Button>
        </Flex>
      </Group>
      <Text fw={500}>현재 경로: {currentPath || "/"}</Text>

      <Flex w="100%" h="100%" p="sm" justify="space-between" gap="md" direction="column">
        <StandardLayoutTableWrapper>
          <ListTable<S3ListGet200ResponseForListTable, S3ListGet200ResponseInner>
            columns={columns}
            isRawData={true}
            rawRows={rows}
            rowKeyGetter={rowKeyGetter}
          />
        </StandardLayoutTableWrapper>
        <PaginationBox<any> />
      </Flex>
    </Flex>
  );
};
